import React, { useState, useEffect, useRef } from "react";
import {
  Tooltip,
  Stack,
  Grid,
  MenuItem,
  Checkbox,
  Button,
  Paper,
  Menu,
  Fade,
  ListItemText,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextareaAutosize,
  Box,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import format from "date-fns/format";
import { toast } from "react-toastify";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { CommonButton } from "../common/commonButton";
import { useStyles } from "./paymentUpdatesContainerStyles";
import { CommonPaginationContainer, CommonFilterSidebar } from "../common";
import instance from "../../redux/api";
import { exceptionReport, premiumBDXExport, premiumBDXMatchDetail } from "../../Services/api";
import "./bdx.css";
import ROUTES from "../../utils/routes";
import ExceptionReportDetails from "./ExceptionReportDetails";
import DemoWorkflowNav from "./DemoWorkflowNav";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import DifferenceOutlinedIcon from "@mui/icons-material/DifferenceOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { getPaginationCount } from "../CashAllocation/utils";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { commonToast } from "../common";
import "../common/commonPageStyles.css";
import {
  primaryButtonStyle,
  primaryButtonHoverColor,
} from "../common/commonButtonStyles";


const options = {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

function handleAmount(val) {
  return (
    (Number(val) && Number(val).toLocaleString("en-US", options)) ||
    (val === 0 && parseInt(val).toFixed(2)) ||
    val
  );
}

// DEMO: status pill
const getExceptionStatusPill = (rawStatus) => {
  const status = String(rawStatus ?? "");
  const lower = status.toLowerCase();
  if (lower.includes("no variance")) {
    return {
      label: "Ready for Approval",
      color: "#9c27b0",
      bg: "rgba(156, 39, 176, 0.1)",
    };
  }
  if (lower.includes("investigate") || lower.includes("review")) {
    return {
      label: "Needs Review",
      color: "#ff9800",
      bg: "rgba(255, 152, 0, 0.1)",
    };
  }
  if (lower.includes("matched")) {
    return {
      label: "Matched",
      color: "#4caf50",
      bg: "rgba(76, 175, 80, 0.1)",
    };
  }
  return {
    label: "Processed",
    color: "#1672DE",
    bg: "rgba(22, 114, 222, 0.1)",
  };
};

// DEMO: money polish — $ prefix, muted zero
const renderExceptionMoneyCell = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) {
    return <span style={{ color: "#9ca3af", fontWeight: 500 }}>—</span>;
  }
  const color = num < 0 ? "#f44336" : "#374151";
  return (
    <span style={{ color, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
      {num < 0 ? "-" : ""}$
      {Math.abs(num).toLocaleString("en-US", options)}
    </span>
  );
};

const ExceptionReport = () => {
  const { userData } = useSelector((state) => state?.user);
  const [searchFieldsParams, setSearchFieldsParams] = useState({
    certificate_ref: "",
    bdx_file_name: "",
    overall_status: [
      // "Investigate Investigate",
      // "Investigate Initiate Payment",
      // "Investigate Partner Payment Done",
      // "Investigate Payment Fully Done"
    ],
  });

  const [paginationCount, setPaginationCount] = useState(0);

  const toggle = useSelector((state) => state.toggleSideMenu);
  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [isClearPerformed, setIsClearPerformed] = useState(false);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);
  // DEMO: CSV export in progress
  const [isExporting, setIsExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  // DEMO: match detail drill-down
  const [matchDetailOpen, setMatchDetailOpen] = useState(false);
  const [matchDetailLoading, setMatchDetailLoading] = useState(false);
  const [matchDetailData, setMatchDetailData] = useState(null);

  const handleOpenMatchDetail = async (row) => {
    if (!row?.id) return;
    setMatchDetailOpen(true);
    setMatchDetailLoading(true);
    setMatchDetailData(null);
    try {
      const response = await instance.get(premiumBDXMatchDetail, {
        params: { id: row.id },
      });
      if (response?.status === 200) {
        setMatchDetailData(response.data ?? null);
      }
    } catch (error) {
      console.log("match detail error", error);
      toast.error("Could not load match details", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
      setMatchDetailOpen(false);
    } finally {
      setMatchDetailLoading(false);
    }
  };

  const handleCloseMatchDetail = () => {
    setMatchDetailOpen(false);
    setMatchDetailData(null);
  };

  const formatMatchAmount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return `${num < 0 ? "-" : ""}$${Math.abs(num).toLocaleString("en-US", options)}`;
  };

  const [selectedParams, setSelectedParams] = useState({
    type_of_payment: "",
    certificate_ref: "",
    bdx_file_name: "",
  });
  const currentSkipNumber = () => {
    return pageState?.page === 1
      ? 0
      : pageState?.page === 0
        ? pageState?.page
        : pageState?.page - 1;
  };
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
  });

  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [overallStatuses, setOverallStatuses] = useState([]);
  const openMenu = Boolean(anchorEl);
  const [openCommentsDialog, setOpenCommentsDialog] = useState(null);
  const [currentRowId, setCurrentRowId] = useState(null);
  const [dialogCommentsValue, setDialogCommentsValue] = useState("");
  const [commentType, setCommentType] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);

  const isFirstRun = useRef(true);

  useEffect(() => {
    getOverallStatusList();
  }, []);

  //styles
  const classes = useStyles();

  const Container = {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 0,
    height: "calc(100vh - 180px)",
    maxHeight: "calc(100vh - 180px)",
    overflow: "hidden",
    backgroundColor: "#fafafa",
  };

  useEffect(() => {
    handleSearchPaymentUpdatesList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFieldsParams.overall_status])


  const getOverallStatusList = async () => {
    const response = await instance.get(
      "payment/payment_overall_status/overall_status/"
    );
    const overall_status_list = response.data.data;
    let selectedOverallStatus = [];
    for (let overall_status of overall_status_list) {
      if (overall_status.toLowerCase().includes("investigate")) {
        selectedOverallStatus.push(overall_status);
      }
    }
    setOverallStatuses(selectedOverallStatus);
    setSearchFieldsParams((old) => ({
      ...old,
      overall_status: selectedOverallStatus,
    }));
  };

  const searchQueryParams = () => {
    let getSearchQueryParams = "";

    const { certificate_ref, bdx_file_name, overall_status } =
      searchFieldsParams;

    if (certificate_ref) {
      getSearchQueryParams = `${getSearchQueryParams}&certificate_ref=${certificate_ref}`;
    }

    if (bdx_file_name) {
      getSearchQueryParams = `${getSearchQueryParams}&bdx_file_name=${bdx_file_name}`;
    }

    if (overall_status?.length) {
      const overallStatusStr = overall_status.join(",");
      getSearchQueryParams = `${getSearchQueryParams}&overall_status=${overallStatusStr}`;
    }

    return getSearchQueryParams;
  }

  const handleSearchPaymentUpdatesList = async () => {
    try {
      let queryParams = `?skip=0&pageSize=${pageState?.pageSize}`;

      const getSearchQueryParam = searchQueryParams();
      if (getSearchQueryParam) {
        queryParams = `${queryParams}${getSearchQueryParam}`;
      }

      // set loading indicator
      setPageState((old) => ({
        ...old,
        isLoading: true,
      }));

      const response = await instance.get(`${exceptionReport}${queryParams}`);

      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));

      setIsSearchPerformed(true);
      setIsClearPerformed(false);

      if (response.status === 200) {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: response?.data?.data ?? [],
          page: 1,  // required for sending skip as 0
        }));
      } else {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: [],
        }));
        setPaginationCount(0);
      }
    } catch (error) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.log("error", error);
    }
  };

  const handleClearSearchList = async () => {
    setIsSearchPerformed(false);
    setIsClearPerformed(true);

    // clear fields
    setSearchFieldsParams({
      certificate_ref: "",
      bdx_file_name: "",
      overall_status: [],
    });
  };

  const isFilterBtnDisable = () => {
    let disabled = true;
    const { certificate_ref, bdx_file_name, overall_status } =
      searchFieldsParams;
    if (
      certificate_ref !== "" ||
      bdx_file_name !== "" ||
      overall_status.length
    ) {
      disabled = false;
    }
    return disabled;
  };

  const loadData = async () => {
    try {
      setPageState((old) => ({
        ...old,
        isLoading: true,
      }));

      let investigate_str = "";
      let queryParams = ``;
      if (searchFieldsParams.overall_status.length) {
        const overallStatusStr = searchFieldsParams.overall_status.join(",");
        queryParams = `${queryParams}&overall_status=${overallStatusStr}`;
      } else if (isFirstRun.current) {
        investigate_str = "investigate";
        queryParams = `&overall_status_contains=${investigate_str}`;
        isFirstRun.current = false;
      }
      // add search query param if the search is enabled
      if (isSearchPerformed) {
        const getSearchQueryParam = searchQueryParams();
        if (getSearchQueryParam) {
          queryParams = `${queryParams}${getSearchQueryParam}`;
        }
      }

      const response = await instance.get(
        `${exceptionReport}?skip=${currentSkipNumber()}&pageSize=${pageState?.pageSize
        }${queryParams}`
      );

      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));

      if (response.status === 200) {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: response?.data?.data ?? [],
        }));
      } else {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: [],
        }));
        setPaginationCount(0);
      }
    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.error("err", err);
    }
  };

  const handleCloseMenu = (index) => {
    setAnchorEl(null);
  };

  const handleClickMenu = (event, params) => {
    setSelectedParams((old) => ({
      ...old,
      certificate_ref: params?.certificate_ref,
      bdx_file_name: params?.bdx_file_name,
      type_of_payment: params?.cash_bank_ref,
    }));
    setAnchorEl(event.currentTarget);
  };

  const handleCheckboxChange = (event, index) => {
    if (event.target.checked) {
      setSelectedRowIndex(index);
      setOpenDetailsDialog(true);
    } else {
      setSelectedRowIndex(null);
      setOpenDetailsDialog(false);
    }
  };


  const getTreasurySearchPrams = () => {
    let searchParam = "";

    if (selectedParams?.certificate_ref) {
      searchParam = `${searchParam}policyNo=${selectedParams?.certificate_ref}`;
    }
    // if (selectedParams?.bdx_file_name) {
    //   searchParam = `${searchParam}&fileName=${selectedParams?.bdx_file_name.replace("&", "%26")}`;
    // }
    // if (selectedParams?.type_of_payment) {
    //   searchParam = `${searchParam}&paymentType=${selectedParams?.type_of_payment}`;
    // }

    return searchParam;
  }

  const PremiumBDXSearchPrams = () => {
    let searchParam = "";

    if (selectedParams?.certificate_ref) {
      searchParam = `${searchParam}policyNo=${selectedParams?.certificate_ref}`;
    }
    if (selectedParams?.bdx_file_name) {
      searchParam = `${searchParam}&fileName=${selectedParams?.bdx_file_name.replace("&", "%26")}`;
    }
    return searchParam;
  }

  const columns = [
    {
      headerName: "",
      headerAlign: "center",
      width: 60,
      type: "actions",
      renderCell: (params) => (
        <>
          <Checkbox
            className="table-checkbox"
            checked={selectedRowIndex === params.api.getRowIndex(params.row.id)}
            onChange={(event) =>
              handleCheckboxChange(event, params.api.getRowIndex(params.row.id))
            }
            sx={{
              color: "#1672DE",
              "&.Mui-checked": {
                color: "#1672DE",
              },
            }}
          />
        </>
      ),
    },
    {
      field: "certificate_ref",
      headerName: "Certificate Ref",
      headerAlign: "center",
      headerClassName: "hide-column",
      width: 210,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 4,
            width: "100%",
          }}
        >
          <Tooltip title={params.row.certificate_ref ?? ""}>
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {params.row.certificate_ref}
            </div>
          </Tooltip>
          {/* DEMO: open BDX vs allocation match breakdown */}
          <Tooltip title="View match details">
            <IconButton
              size="small"
              onClick={() => handleOpenMatchDetail(params.row)}
              sx={{ color: "#1672DE", padding: "2px", flexShrink: 0 }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            onClick={(event) => handleClickMenu(event, params.row)}
            endIcon={<KeyboardArrowDownIcon style={{ fill: "black" }} />}
            style={{ minWidth: "20px", background: "transparent", flexShrink: 0 }}
          />
          <Paper>
            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleCloseMenu}
              TransitionComponent={Fade}
            >
              <MenuItem onClick={{}} style={{ color: "black" }}>
                <Link
                  to={{
                    pathname: ROUTES.MUCASHTRACKER,
                    search: `?policyNo=${selectedParams.certificate_ref}`,
                  }}
                  target={"_blank"}
                  style={{ color: "black" }}
                >
                  Cash Tracker
                </Link>
              </MenuItem>
              <MenuItem onClick={{}}>
                <Link
                  to={{
                    pathname: ROUTES.TreasuryList,
                    search: `?${getTreasurySearchPrams()}`,
                  }}
                  target={"_blank"}
                  style={{ color: "black" }}
                >
                  Treasury
                </Link>
              </MenuItem>
              <MenuItem onClick={{}}>
                <Link
                  to={{
                    pathname: ROUTES.PremiumBDX,
                    search: `?${PremiumBDXSearchPrams()}`,
                  }}
                  params={{
                    policyNo: params.row.certificateRef,
                    fileName: params.row.bdx_file_name,
                  }}
                  target={"_blank"}
                  style={{ color: "black" }}
                >
                  Premium BDX
                </Link>
              </MenuItem>
            </Menu>
          </Paper>
        </div>
      ),
    },
    {
      field: "bdx_file_name",
      headerName: "BDX File Name",
      headerAlign: "center",
      width: 500,
      renderCell: (params) => (
        <div>
          <Tooltip title={params.row.bdx_file_name}>
            {params.row?.bdx_file_name.length > 100 
                ? `${params.row.bdx_file_name.substring(0, 100)}...` 
                : params.row.bdx_file_name}
          </Tooltip>
        </div>
      ),
    },
    {
      field: "created_at",
      headerName: "Created Date",
      headerAlign: "center",
      width: 170,
      valueFormatter: (params) => {
        return format(new Date(params.value), "dd-MM-yyyy");
      },
    },
    {
      field: "settlement_currency",
      headerName: "Settlement Ccy",
      headerAlign: "center",
      width: 130
    },
    {
      field: "net_of_brokerage",
      headerName: "Net of Brokerage",
      headerAlign: "center",
      width: 130,
      cellClassName: "amount-table-cell",
      // DEMO: money polish
      renderCell: (params) => renderExceptionMoneyCell(params.row.net_of_brokerage),
    },
    {
      field: "net_premium",
      headerName: "Net Premium",
      headerAlign: "center",
      width: 110,
      cellClassName: "amount-table-cell",
      // DEMO: money polish
      renderCell: (params) => renderExceptionMoneyCell(params.row.net_premium),
    },
    {
      // DEMO: shorter headers
      field: "ct_allocation_amt",
      headerName: "CT Alloc",
      description: "CT Allocation Amt",
      headerAlign: "center",
      width: 120,
      cellClassName: "amount-table-cell",
      // DEMO: money polish
      renderCell: (params) => renderExceptionMoneyCell(params.row.ct_allocation_amt),
    },
    {
      // DEMO: variance color
      field: "variance_with_ct",
      headerName: "Variance",
      headerAlign: "center",
      width: 130,
      cellClassName: "amount-table-cell",
      renderCell: (params) => {
        const raw = params.row?.variance_with_ct;
        const num = Number(raw);
        if (!Number.isFinite(num) || num === 0) {
          return <span style={{ color: "#9ca3af", fontWeight: 500 }}>—</span>;
        }
        const absFormatted = Math.abs(num).toLocaleString("en-US", options);
        if (num < 0) {
          return (
            <span style={{ color: "#f44336", fontWeight: 700 }}>
              -${absFormatted}
            </span>
          );
        }
        return (
          <span style={{ color: "#4caf50", fontWeight: 700 }}>
            +${absFormatted}
          </span>
        );
      },
    },
    {
      field: "overall_status",
      headerName: "Overall Status",
      headerAlign: "center",
      width: 180,
      cellClassName: "overall-status-cell",
      renderCell: (params) => {
        // DEMO: status pill
        const rawStatus = params.row?.overall_status;
        const { label, color, bg } = getExceptionStatusPill(rawStatus);
        return (
          <Tooltip title={rawStatus || ""}>
            <Chip
              label={label}
              sx={{
                backgroundColor: bg,
                color,
                fontWeight: "600",
                fontSize: "12px",
                height: "28px",
                borderRadius: "6px",
                border: `1px solid ${color}`,
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      // DEMO: shorter headers
      field: "cash_bdx_amt",
      headerName: "Cash BDx",
      description: "Cash BDx Amt",
      headerAlign: "center",
      width: 115,
      cellClassName: "amount-table-cell",
      // DEMO: money polish
      renderCell: (params) => renderExceptionMoneyCell(params?.row?.cash_bdx_amt),
    },
    {
      // DEMO: shorter headers
      field: "non_cash_bdx_amt",
      headerName: "Non-Cash BDx",
      description: "Non Cash BDx Amt",
      headerAlign: "center",
      width: 130,
      cellClassName: "amount-table-cell",
      // DEMO: money polish
      renderCell: (params) => renderExceptionMoneyCell(params?.row?.non_cash_bdx_amt),
    },

    {
      // DEMO: shorter headers
      field: "potential_payment_indicator",
      headerName: "Payment Ind",
      description: "Potential Payment Indicator",
      headerAlign: "center",
      width: 120,
    },
    {
      field: "comments_audit",
      headerName: "Comments - Audit",
      headerAlign: "center",
      width: 140,
      renderCell: (params) => (
        <>
          {params?.row?.comments_audit?.substr(0, 15) ?? ""}
          <div>
            <MoreHorizIcon
              onClick={() =>
                handleCommentsOpenDialog(
                  params?.row?.id,
                  params?.row?.comments_audit,
                  "comments_audit"
                )
              }
            />
          </div>
        </>
      ),
    },
    {
      field: "comment",
      headerName: "Comment",
      headerAlign: "center",
      width: 120,
      renderCell: (params) => (
        <>
          {params?.row?.comment?.substr(0, 15) ?? ""}
          <div>
            <MoreHorizIcon
              onClick={() =>
                handleCommentsOpenDialog(
                  params?.row?.id,
                  params?.row?.comment,
                  "comment"
                )
              }
            />
          </div>
        </>
      ),
    },
  ];


  useEffect(() => {
    setPaginationCount(getPaginationCount(pageState?.total, pageState?.pageSize));
  }, [pageState?.pageSize, pageState?.total]);

  // UseEffect
  useEffect(() => {
    if (isClearPerformed) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClearPerformed]);

  useEffect(() => {
    if (!isClearPerformed) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState?.pageSize, pageState?.page]);

  const handleCommentsOpenDialog = (currentRowId, currentRowCommentData, commentType) => {
    setOpenCommentsDialog(true);
    setCurrentRowId(currentRowId);
    setDialogCommentsValue(currentRowCommentData);
    setCommentType(commentType);
  };

  const handleCloseCommentDialog = () => {
    setOpenCommentsDialog(false);
    setDialogCommentsValue("");
    setCurrentRowId(null);
    setCommentType(null);
  };

  const handleDialogInputChange = (e) => {
    setDialogCommentsValue(e.target.value);
  };

  const handleSaveComments = () => {
    if (commentType === "comment") {
      handleCommentsUpdate();
    }
    else if (commentType === "comments_audit") {
      handleAuditCommentsUpdate();
    }
    handleCloseCommentDialog();
  };

  const handleCommentsUpdate = async () => {
    const payload = {
      updated_by: userData.id,
      comment: dialogCommentsValue,
    };
    const response = await instance.patch(
      `/payment/exception_file/${currentRowId}/`,
      payload
    );
    if (response.status === 200) {
      commonToast("success", "Comment successfully saved");
      loadData();
    } else {
      commonToast("error", "Comment could not be saved");
    }
  };

  const handleAuditCommentsUpdate = async () => {
    const payload = {
      updated_by: userData.id,
      comments_audit: dialogCommentsValue,
    };
    const response = await instance.patch(
      `/payment/exception_file/${currentRowId}/`,
      payload
    );
    if (response.status === 200) {
      commonToast("success", "Audit Comment successfully saved");
      loadData();
    } else {
      commonToast("error", "Audit Comment could not be saved");
    }
  };

  const handleEnter = (e) => {
    if (e.code === "Enter") {
      handleSearchPaymentUpdatesList();
    }
  };

  // Prepare filter configs for CommonFilterSidebar
  const filterConfigs = [
    {
      type: "textInput",
      label: "Certificate Ref",
      placeholder: "Type certificate ref",
      value: searchFieldsParams.certificate_ref,
      onChange: (value) => {
        if (!value) {
          handleClearSearchList();
        }
        setSearchFieldsParams((v) => ({
          ...v,
          certificate_ref: value,
        }));
      },
      onKeyUp: handleEnter,
    },
    {
      type: "textInput",
      label: "BDX File Name",
      placeholder: "Type bdx file name",
      value: searchFieldsParams.bdx_file_name,
      onChange: (value) => {
        if (!value) {
          handleClearSearchList();
        }
        setSearchFieldsParams((v) => ({
          ...v,
          bdx_file_name: value,
        }));
      },
      onKeyUp: handleEnter,
    },
    {
      type: "custom",
      content: (
        <>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#000",
              marginBottom: "12px",
                }}
              >
                Overall Status
          </Typography>
              <Select
            fullWidth
            size="small"
                name="overall_status"
                multiple
                value={searchFieldsParams.overall_status}
            onChange={(e) => {
              const value = e.target.value;
              if (!value || value.length === 0) {
                handleClearSearchList();
              }
              setSearchFieldsParams((v) => ({
                ...v,
                overall_status: value,
              }));
            }}
            renderValue={(selected) => 
              selected.length > 0 
                ? selected.join(", ").substring(0, 30) + (selected.join(", ").length > 30 ? "..." : "")
                : "Select Overall Status"
            }
            sx={{
              color: "#000000",
              "& .MuiOutlinedInput-root": {
                borderRadius: "4px",
                color: "#000000",
                "& fieldset": {
                  borderColor: "#d0d0d0",
                },
                "&:hover fieldset": {
                  borderColor: "#1672DE",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1672DE",
                },
              },
              "& .MuiSelect-icon": {
                color: "#1672DE",
              },
              "& .MuiInputBase-input": {
                color: "#000000",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    "&:hover": {
                      backgroundColor: "rgba(22, 114, 222, 0.08)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(22, 114, 222, 0.12)",
                      "&:hover": {
                        backgroundColor: "rgba(22, 114, 222, 0.16)",
                      },
                    },
                  },
                },
              },
            }}
              >
                {overallStatuses.map((name) => (
              <MenuItem 
                key={name} 
                value={name}
                sx={{
                  color: "#000000",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.08)",
                    color: "#000000",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(22, 114, 222, 0.12)",
                    color: "#000000",
                    "&:hover": {
                      backgroundColor: "rgba(22, 114, 222, 0.16)",
                      color: "#000000",
                    },
                  },
                }}
              >
                    <Checkbox
                  checked={searchFieldsParams?.overall_status?.indexOf(name) > -1}
                  sx={{
                    color: "#1672DE",
                    "&.Mui-checked": {
                      color: "#1672DE",
                    },
                  }}
                />
                <ListItemText 
                  primary={name}
                  primaryTypographyProps={{
                    sx: {
                      color: "#000000",
                    },
                  }}
                />
                  </MenuItem>
                ))}
              </Select>
        </>
      ),
    },
  ];

  // DEMO: summary line
  const loadedRows = pageState?.data ?? [];
  const demoTotalExceptions = loadedRows.length;
  const demoNeedsReview = loadedRows.filter((row) => {
    const status = String(row?.overall_status ?? "").toLowerCase();
    return status.includes("investigate") || status.includes("review");
  }).length;
  const demoTotalVariance = loadedRows.reduce((sum, row) => {
    const n = Number(row?.variance_with_ct);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const demoTotalVarianceFormatted = `${demoTotalVariance < 0 ? "-" : ""}$${Math.abs(
    demoTotalVariance,
  ).toLocaleString("en-US", options)}`;
  const demoMatched = Math.max(demoTotalExceptions - demoNeedsReview, 0);

  // DEMO: summary cards to match the Premium BDX dashboard styling
  const exceptionSummaryCards = [
    {
      label: "Total Exceptions",
      value: demoTotalExceptions,
      sub: "certificates reviewed",
      color: "#1672DE",
      Icon: FactCheckOutlinedIcon,
    },
    {
      label: "Matched",
      value: demoMatched,
      sub: demoTotalExceptions > 0 ? `${Math.round((demoMatched / demoTotalExceptions) * 100)}% no variance` : "—",
      color: "#4caf50",
      Icon: TaskAltIcon,
    },
    {
      label: "Needs Review",
      value: demoNeedsReview,
      sub: demoNeedsReview > 0 ? "requires action" : "all clear",
      color: "#ff9800",
      Icon: ReportProblemOutlinedIcon,
      highlight: demoNeedsReview > 0,
    },
    {
      label: "Total Variance",
      value: demoTotalVarianceFormatted,
      sub: "vs cash allocation",
      color: demoTotalVariance === 0 ? "#4caf50" : "#f44336",
      Icon: DifferenceOutlinedIcon,
    },
  ];

  // DEMO: download the exception rows as CSV, optionally filtered by status
  const handleExportExceptions = async (statusFilter = "all") => {
    setExportMenuAnchor(null);
    try {
      setIsExporting(true);
      const params = {};
      if (searchFieldsParams?.bdx_file_name) {
        params.bdx_file_name = searchFieldsParams.bdx_file_name;
      }
      if (statusFilter && statusFilter !== "all") {
        params.status = statusFilter;
      }
      const response = await instance.get(premiumBDXExport, {
        responseType: "blob",
        params,
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      const suffix =
        statusFilter === "matched"
          ? "matched"
          : statusFilter === "needs_review"
            ? "needs_review"
            : "all";
      link.setAttribute(
        "download",
        `premium_bdx_${suffix}_${format(new Date(), "yyyy-MM-dd")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Exception report downloaded", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
    } catch (error) {
      console.log("export error", error);
      toast.error("Could not download the exception report", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div style={Container}>
      {/* Filter Sidebar */}
      <CommonFilterSidebar
        isOpen={filterSidebarOpen}
        filterConfigs={filterConfigs}
        onApplyFilters={handleSearchPaymentUpdatesList}
        onClearFilters={handleClearSearchList}
        isLoading={pageState?.isLoading}
        isFilterDisabled={isFilterBtnDisable()}
      />

      {/* Main Content Area */}
      <Box className="main-content-area">
        <Box className="page-header-container">
          <Box>
            <Typography
              variant="h5"
              className="page-heading"
            >
              Exception Report
            </Typography>
            {/* DEMO: role context line */}
            <Typography
              sx={{
                fontSize: "12px",
                color: "#6b7280",
                marginTop: "4px",
                fontWeight: 500,
              }}
            >
              Analyst view — investigate unmatched certificates
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {/* DEMO: export exception rows to CSV (split by status) */}
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              disabled={isExporting}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "8px",
                color: "#1672DE",
                borderColor: "#1672DE",
                "&:hover": {
                  borderColor: primaryButtonHoverColor,
                  backgroundColor: "rgba(22, 114, 222, 0.06)",
                },
              }}
            >
              {isExporting ? "Exporting..." : "Export CSV"}
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
              TransitionComponent={Fade}
            >
              <MenuItem onClick={() => handleExportExceptions("all")}>
                Export all ({demoTotalExceptions})
              </MenuItem>
              <MenuItem onClick={() => handleExportExceptions("matched")}>
                Matched only ({demoMatched})
              </MenuItem>
              <MenuItem onClick={() => handleExportExceptions("needs_review")}>
                Needs review only ({demoNeedsReview})
              </MenuItem>
            </Menu>
            <IconButton
              onClick={() => setFilterSidebarOpen(!filterSidebarOpen)}
              className="filter-toggle-button"
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* DEMO: guided workflow path */}
        <DemoWorkflowNav activeStep={1} />

        {/* DEMO: summary cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: "12px",
            marginBottom: "12px",
          }}
        >
          {exceptionSummaryCards.map((card) => {
            const CardIcon = card.Icon;
            return (
              <Paper
                key={card.label}
                elevation={0}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  padding: "16px 18px",
                  borderRadius: "14px",
                  border: "1px solid #e8eaf0",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    backgroundColor: card.color,
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontSize: "26px", fontWeight: 700, color: card.color, lineHeight: 1.2 }}>
                      {card.value}
                    </Typography>
                    {card.sub && (
                      <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.25 }}>
                        {card.sub}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: `${card.color}1A`,
                      flexShrink: 0,
                    }}
                  >
                    <CardIcon sx={{ color: card.color, fontSize: "22px" }} />
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>

        <Box
          sx={{
            flex: 1,
          display: "flex",
            flexDirection: "column",
            minHeight: 0,
            position: "relative",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            overflow: "visible",
            transition: "all 0.3s ease",
            border: "1px solid #e8eaf0",
            paddingBottom: "10px",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            },
        }}
      >
        <DataGrid
          className={classes.pagination}
          loading={pageState?.isLoading}
          rows={pageState?.data ?? []}
          getRowId={(row) => row?.id}
          rowCount={pageState?.total}
          columns={columns}
          keepNonExistentRowsSelected
          pagination
          paginationMode="server"
          disableSelectionOnClick
          page={pageState.page - 1}
          pageSize={pageState?.pageSize ?? 25}
            rowsPerPageOptions={[10, 25, 50, 100]}
          onPageSizeChange={(newPageSize) => {
            return setPageState((old) => {
              setPaginationCount(getPaginationCount(pageState?.total, newPageSize))
              return { ...old, pageSize: newPageSize, page: 1 };
            });
          }}
          sx={{
              border: "none",
              flex: 1,
              minHeight: 0,
              "& .MuiDataGrid-root": {
                border: "none",
                height: "100%",
              },
              "& .MuiDataGrid-main": {
                height: "100%",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#1672DE",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#1565c0",
                  },
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #e0e0e0",
                minHeight: "52px !important",
                maxHeight: "52px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "transparent",
                fontWeight: "600",
                color: "#1672DE",
                fontSize: "14px",
                padding: "12px 16px",
                borderBottom: "none",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "600",
                  fontSize: "14px",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#1672DE",
                },
                "& .MuiDataGrid-menuIcon": {
                  color: "#1672DE",
                },
              },
              "& .MuiDataGrid-row": {
                transition: "all 0.2s ease",
                borderBottom: "1px solid #f0f0f0",
                "&:hover": {
                  backgroundColor: "rgba(22, 114, 222, 0.04)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(22, 114, 222, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.12)",
                  },
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "#fafafa",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.04)",
                  },
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
                padding: "12px 16px",
                fontSize: "13px",
                color: "#333",
                display: "flex",
                alignItems: "center",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
              },
              // DEMO: softer scrollbar
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "auto !important",
                "&::-webkit-scrollbar": {
                  width: "6px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#cbd5e1",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#94a3b8",
                  },
                },
                scrollbarColor: "#cbd5e1 transparent",
                scrollbarWidth: "thin",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid #e0e0e0",
                backgroundColor: "#f8f9fa",
                minHeight: "48px",
                maxHeight: "48px",
                paddingBottom: "8px",
                paddingTop: "8px",
                paddingLeft: "16px",
                paddingRight: "16px",
                position: "relative",
                zIndex: 1,
                "& .MuiTablePagination-root": {
                  color: "#666",
                  paddingRight: "16px",
                  margin: 0,
                  minHeight: "32px",
                },
                "& .MuiTablePagination-selectLabel": {
                  fontSize: "13px",
                  margin: 0,
                },
                "& .MuiTablePagination-displayedRows": {
                  fontSize: "13px",
                  margin: 0,
                },
                "& .MuiTablePagination-actions": {
                  marginLeft: "8px",
                  "& .MuiIconButton-root": {
                    padding: "4px",
                  },
                },
                "& .MuiTablePagination-select": {
                  margin: "0 8px",
                  padding: "4px 8px",
                },
                "& .MuiTablePagination-toolbar": {
                  minHeight: "32px",
                  padding: 0,
                },
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "12px 16px",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #e0e0e0",
                "& .MuiButton-root": {
                  color: "#1672DE",
                  fontSize: "13px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.1)",
                  },
                },
              },
              "& .MuiDataGrid-loadingOverlay": {
                backgroundColor: "rgba(255, 255, 255, 0.8)",
            },
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          components={{
            Toolbar: GridToolbarContainer,
            NoRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ color: "#999" }}
                >
                  <Typography variant="body2" sx={{ color: "#999" }}>
                No Uploaded Files
                  </Typography>
              </Stack>
            ),
          }}
        />
        <Stack
            sx={{
            position: "absolute",
              bottom: "16px",
              right: "20px",
              zIndex: 10,
              backgroundColor: "#f8f9fa",
              padding: "4px 8px",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <CommonPaginationContainer
            count={paginationCount + 1}
            pageState={pageState}
            setPageState={setPageState}
          />
        </Stack>
        </Box>
      </Box>

      <Dialog open={openCommentsDialog} onClose={handleCloseCommentDialog}>
        <DialogTitle>{commentType === "comment" ? "Comment" : commentType === "comments_audit" ? "Comments - Audit" : ""}</DialogTitle>
        <DialogContent sx={{ overflow: "hidden", textAlign: "center" }}>
          <TextareaAutosize
            minRows={5}
            value={dialogCommentsValue}
            onChange={handleDialogInputChange}
            className=""
            style={{ width: "350px", minWidth: "350px" }}
          />
        </DialogContent>

        <DialogActions>
          <CommonButton
            text="Cancel"
            handleClick={handleCloseCommentDialog}
            style={{
              backgroundColor: "#ffffff",
              color: "#000000",
              border: "1px solid #BCBDBF",
              borderRadius: "6px",
              padding: "8px 16px",
              fontWeight: "500",
            }}
          />
          <CommonButton
            text="Save"
            handleClick={handleSaveComments}
            sx={primaryButtonStyle}
            hoverColor={primaryButtonHoverColor}
            disabled={!dialogCommentsValue}
          />
        </DialogActions>
      </Dialog>

      {/* Exception Report Details Dialog */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={() => {
          setOpenDetailsDialog(false);
          setSelectedRowIndex(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle sx={{ 
          fontSize: "20px", 
          fontWeight: "600", 
          color: "#000",
          paddingBottom: "12px",
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f8f9fa",
        }}>
          Exception Report Details
        </DialogTitle>
        <DialogContent sx={{ 
          paddingTop: "20px", 
          paddingBottom: "20px",
          maxHeight: "70vh",
          overflowY: "auto",
        }}>
          {selectedRowIndex != null && pageState?.data[selectedRowIndex] && (
            <ExceptionReportDetails record={pageState?.data[selectedRowIndex]} />
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", borderTop: "1px solid #e0e0e0" }}>
          <CommonButton
            text="Close"
            handleClick={() => {
              setOpenDetailsDialog(false);
              setSelectedRowIndex(null);
            }}
            sx={primaryButtonStyle}
            hoverColor={primaryButtonHoverColor}
          />
        </DialogActions>
      </Dialog>

      {/* DEMO: match detail drill-down — BDX vs cash allocation */}
      <Dialog
        open={matchDetailOpen}
        onClose={handleCloseMatchDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #e8eaf0" }}>
          Match Detail
          {matchDetailData?.certificate_ref && (
            <Typography component="span" sx={{ fontSize: "14px", fontWeight: 500, color: "#6b7280", ml: 1 }}>
              {matchDetailData.certificate_ref}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {matchDetailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={36} sx={{ color: "#1672DE" }} />
            </Box>
          ) : matchDetailData ? (
            <Box>
              <Typography sx={{ fontSize: "13px", color: "#6b7280", mb: 2 }}>
                {matchDetailData.bdx_file_name} · {matchDetailData.settlement_currency ?? "—"}
              </Typography>

              {/* DEMO: before/after reconciliation story */}
              {(() => {
                const bdxNet = Number(matchDetailData.bdx?.net_premium);
                const cashAllocated = Number(matchDetailData.allocation?.allocated_amount);
                const diff = Number(matchDetailData.variance_with_ct);
                const noVariance = Number.isFinite(diff) && diff === 0;
                const cur = matchDetailData.settlement_currency ?? "";
                return (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      p: 2,
                      mb: 2,
                      borderRadius: "10px",
                      border: `1px solid ${noVariance ? "#c8e6c9" : "#ffcdd2"}`,
                      backgroundColor: noVariance ? "#f1f8f2" : "#fff5f5",
                    }}
                  >
                    <Box sx={{ textAlign: "center", flex: 1, minWidth: 90 }}>
                      <Typography sx={{ fontSize: "10.5px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        BDX expected
                      </Typography>
                      <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#1672DE" }}>
                        {formatMatchAmount(bdxNet)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "18px", color: "#9ca3af", fontWeight: 600 }}>vs</Typography>
                    <Box sx={{ textAlign: "center", flex: 1, minWidth: 90 }}>
                      <Typography sx={{ fontSize: "10.5px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Cash allocated
                      </Typography>
                      <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#ff9800" }}>
                        {formatMatchAmount(cashAllocated)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "18px", color: "#9ca3af", fontWeight: 600 }}>=</Typography>
                    <Box sx={{ textAlign: "center", flex: 1, minWidth: 90 }}>
                      <Typography sx={{ fontSize: "10.5px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Difference
                      </Typography>
                      <Typography sx={{ fontSize: "17px", fontWeight: 700, color: noVariance ? "#4caf50" : "#f44336" }}>
                        {formatMatchAmount(diff)}
                      </Typography>
                    </Box>
                    <Box sx={{ width: "100%", textAlign: "center" }}>
                      <Typography sx={{ fontSize: "12px", color: noVariance ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                        {noVariance
                          ? "Matched — BDX and cash allocation agree, ready for payment."
                          : `Needs review — BDX and cash differ by ${formatMatchAmount(Math.abs(diff))} ${cur}.`}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid #e8eaf0",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1672DE", mb: 1.5, textTransform: "uppercase" }}>
                    BDX File
                  </Typography>
                  {[
                    ["Gross Premium", matchDetailData.bdx?.gross_premium],
                    ["Brokerage", matchDetailData.bdx?.brokerage],
                    ["Tax", matchDetailData.bdx?.tax],
                    ["Net Premium", matchDetailData.bdx?.net_premium],
                  ].map(([label, val]) => (
                    <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                      <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>{label}</Typography>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{formatMatchAmount(val)}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid #e8eaf0",
                    backgroundColor: "#fffbf5",
                  }}
                >
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#ff9800", mb: 1.5, textTransform: "uppercase" }}>
                    Cash Allocation
                  </Typography>
                  {[
                    ["Status", matchDetailData.allocation?.status ?? "—"],
                    ["Allocated Amount", matchDetailData.allocation?.allocated_amount],
                    ["Bank Charges", matchDetailData.allocation?.bank_charges],
                    [
                      "Allocation Date",
                      matchDetailData.allocation?.allocation_date
                        ? format(new Date(matchDetailData.allocation.allocation_date), "dd-MM-yyyy")
                        : "—",
                    ],
                  ].map(([label, val]) => (
                    <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                      <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>{label}</Typography>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                        {label === "Status" || label === "Allocation Date" ? val : formatMatchAmount(val)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  p: 2,
                  borderRadius: "10px",
                  backgroundColor: "#f3f4f6",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase" }}>Variance</Typography>
                  <Typography sx={{ fontSize: "18px", fontWeight: 700, color: Number(matchDetailData.variance_with_ct) === 0 ? "#4caf50" : "#f44336" }}>
                    {formatMatchAmount(matchDetailData.variance_with_ct)}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase" }}>Overall Status</Typography>
                  <Chip
                    size="small"
                    label={getExceptionStatusPill(matchDetailData.overall_status).label}
                    sx={{
                      mt: 0.5,
                      backgroundColor: getExceptionStatusPill(matchDetailData.overall_status).bg,
                      color: getExceptionStatusPill(matchDetailData.overall_status).color,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase" }}>Payment Indicator</Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                    {matchDetailData.potential_payment_indicator ?? "—"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ py: 2, color: "#6b7280" }}>No match detail available.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CommonButton
            text="Close"
            handleClick={handleCloseMatchDetail}
            sx={primaryButtonStyle}
            hoverColor={primaryButtonHoverColor}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ExceptionReport;
